import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FiAlertTriangle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type { AdminAllTimeMetricsResponse, AdminAllTimePeriodPoint } from '@/lib/types';

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(value: number | null | undefined, currency: string | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

function formatAxisDate(ymd: string, showYear: boolean): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(showYear ? { year: '2-digit' } : {}),
  });
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function yAxisTicks(max: number): number[] {
  const top = niceAxisMax(max);
  const step =
    top <= 5 ? 1 : top <= 10 ? 2 : top <= 25 ? 5 : top <= 50 ? 10 : Math.ceil(top / 4 / 10) * 10;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return ticks;
}

function xLabelIndices(count: number): number[] {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, i) => i);
  const targetLabels = count <= 31 ? 7 : 10;
  const step = Math.max(1, Math.round((count - 1) / (targetLabels - 1)));
  const indices: number[] = [0];
  for (let i = step; i < count - 1; i += step) indices.push(i);
  if (indices[indices.length - 1] !== count - 1) indices.push(count - 1);
  return indices;
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-nova-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

function resolutionLabel(resolution: string | null | undefined): string {
  if (resolution === 'month') return 'Monthly buckets';
  if (resolution === 'week') return 'Weekly buckets';
  return 'Daily buckets';
}

function CumulativeRevenueChart({
  periods,
  currency,
  resolution,
}: {
  periods: AdminAllTimePeriodPoint[];
  currency: string | null;
  resolution: string | null | undefined;
}) {
  const chartWidth = 900;
  const chartHeight = 340;
  const padding = { top: 20, right: 16, bottom: 48, left: 72 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;

  const maxValue = Math.max(...periods.map((d) => d.cumulative), 0);
  const axisMax = niceAxisMax(maxValue);
  const ticks = yAxisTicks(axisMax);
  const showYear = periods.length > 60;
  const labelIndices = new Set(xLabelIndices(periods.length));

  const points = periods.map((d, i) => {
    const x =
      periods.length <= 1
        ? padding.left + innerW / 2
        : padding.left + (i / (periods.length - 1)) * innerW;
    const y = padding.top + innerH - (d.cumulative / axisMax) * innerH;
    return { ...d, x, y };
  });

  const linePath =
    points.length >= 2
      ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
      : '';

  const baselineY = padding.top + innerH;
  const areaPath =
    points.length >= 2
      ? `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`
      : '';

  return (
    <div className='overflow-x-auto'>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className='min-w-[640px] w-full'
        role='img'
        aria-label='All-time cumulative revenue'
      >
        {ticks.map((tick) => {
          const y = padding.top + innerH - (tick / axisMax) * innerH;
          return (
            <g key={tick}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + innerW}
                y2={y}
                stroke='#e5e7eb'
                strokeWidth={1}
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor='end'
                className='fill-gray-400 text-[10px]'
              >
                {formatCurrency(tick, currency)}
              </text>
            </g>
          );
        })}

        {areaPath ? (
          <path d={areaPath} fill='#018A9D' fillOpacity={0.12} stroke='none' />
        ) : null}

        {linePath ? (
          <path
            d={linePath}
            fill='none'
            stroke='#018A9D'
            strokeWidth={2.5}
            strokeLinejoin='round'
            strokeLinecap='round'
          />
        ) : null}

        {points.map((p) => (
          <circle
            key={p.date}
            cx={p.x}
            cy={p.y}
            r={3.5}
            fill='#018A9D'
            stroke='white'
            strokeWidth={1.5}
          >
            <title>
              {formatDateLabel(p.date)}: {formatCurrency(p.cumulative, currency)} cumulative
              {' · '}
              {formatCurrency(p.revenue, currency)} in period
            </title>
          </circle>
        ))}

        {periods.map((d, i) =>
          labelIndices.has(i) ? (
            <text
              key={d.date}
              x={points[i]?.x ?? 0}
              y={chartHeight - 12}
              textAnchor='middle'
              className='fill-gray-500 text-[10px]'
            >
              {formatAxisDate(d.date, showYear)}
            </text>
          ) : null,
        )}

        <text
          x={padding.left + innerW / 2}
          y={chartHeight - 2}
          textAnchor='middle'
          className='fill-gray-500 text-[11px] font-medium'
        >
          {resolutionLabel(resolution)} (Pacific)
        </text>
      </svg>
    </div>
  );
}

function AllTimeSourceBanner({ data }: { data: AdminAllTimeMetricsResponse }) {
  if (!data.env_configured) {
    return (
      <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
        <div className='min-w-0 space-y-1'>
          <p className='font-semibold'>RevenueCat Charts API is not configured</p>
          <p className='text-xs'>
            Set <span className='font-mono'>REVENUECAT_CHARTS_API_KEY</span> and{' '}
            <span className='font-mono'>REVENUECAT_PROJECT_ID</span> on the API deployment.
          </p>
        </div>
      </div>
    );
  }

  if (!data.ok) {
    return (
      <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
        <div className='min-w-0 space-y-1'>
          <p className='font-semibold'>All-time revenue unavailable</p>
          <p className='text-xs'>{data.error ?? 'Could not load revenue from RevenueCat.'}</p>
        </div>
      </div>
    );
  }

  if (data.cache_status === 'stale') {
    return (
      <div className='rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-2.5 text-xs text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' aria-hidden />
        <p>
          <span className='font-semibold'>Cached all-time data</span>
          {data.error ? ` — ${data.error}` : ''}
          {data.cached_at ? ` · cached ${formatDateTime(data.cached_at)}` : ''}
        </p>
      </div>
    );
  }

  return (
    <p className='text-[11px] text-nova-muted-foreground'>
      RevenueCat revenue chart · {resolutionLabel(data.resolution)}
      {data.revenuecat_last_computed_at
        ? ` · RC computed ${formatDateTime(data.revenuecat_last_computed_at)}`
        : ''}
      {data.cached_at && data.cache_status !== 'bypass'
        ? ` · cached ${formatDateTime(data.cached_at)} (${data.cache_status})`
        : data.cache_status === 'bypass'
          ? ' · live fetch'
          : ''}
    </p>
  );
}

function AdminAllTimePage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const allTimeQuery = useQuery({
    queryKey: ['admin', 'all-time'],
    queryFn: () =>
      apiJson<AdminAllTimeMetricsResponse>(getToken, '/v1/admin/metrics/all-time'),
  });

  const data = allTimeQuery.data;
  const currency = data?.currency ?? 'USD';

  const latestPeriod = data?.periods[data.periods.length - 1];

  const averagePeriodRevenue = useMemo(() => {
    if (!data?.periods.length) return null;
    const total = data.periods.reduce((sum, p) => sum + p.revenue, 0);
    return Math.round((total / data.periods.length) * 100) / 100;
  }, [data?.periods]);

  const handleRefresh = () => {
    void queryClient.fetchQuery({
      queryKey: ['admin', 'all-time'],
      queryFn: () =>
        apiJson<AdminAllTimeMetricsResponse>(
          getToken,
          '/v1/admin/metrics/all-time?refresh=true',
        ),
    });
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-nova-foreground sm:text-2xl'>All Time</h1>
          <p className='mt-1 text-sm text-nova-muted-foreground'>
            Lifetime cumulative revenue for Harbor from RevenueCat — running total since the app
            began generating revenue.
          </p>
        </div>
        <button
          type='button'
          onClick={handleRefresh}
          disabled={allTimeQuery.isFetching}
          className='inline-flex items-center gap-1.5 self-end rounded-md border border-nova-input bg-nova-card px-3 py-2 text-xs font-semibold text-nova-foreground hover:bg-nova-accent disabled:opacity-60'
        >
          <FiRefreshCw
            className={`h-3.5 w-3.5 ${allTimeQuery.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      {allTimeQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-nova-primary border-t-transparent' />
        </div>
      ) : allTimeQuery.isError || !data ? (
        <p className='text-red-600'>Could not load all-time revenue metrics.</p>
      ) : (
        <>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nova-muted-foreground'>
            <span className='inline-flex items-center rounded-md bg-nova-primary/10 px-2.5 py-1 text-xs font-semibold text-nova-primary'>
              Since {data.start_date}
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <FiInfo className='h-3.5 w-3.5 shrink-0' aria-hidden />
              {data.first_revenue_date
                ? `First revenue ${data.first_revenue_date} → ${data.end_date}`
                : `${data.start_date} → ${data.end_date}`}{' '}
              ({data.timezone}) · {resolutionLabel(data.resolution)}
            </span>
          </div>

          <AllTimeSourceBanner data={data} />

          {data.ok && data.periods.length ? (
            <>
              <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                <StatTile
                  label='Total revenue (all time)'
                  value={formatCurrency(data.total_revenue, currency)}
                  hint={
                    latestPeriod
                      ? `Through ${formatDateLabel(latestPeriod.date)}`
                      : undefined
                  }
                />
                <StatTile
                  label='Latest period revenue'
                  value={formatCurrency(latestPeriod?.revenue, currency)}
                  hint={latestPeriod ? formatDateLabel(latestPeriod.date) : undefined}
                />
                <StatTile
                  label={`Average ${data.resolution ?? 'period'} revenue`}
                  value={formatCurrency(averagePeriodRevenue, currency)}
                  hint={`Across ${data.periods.length.toLocaleString()} periods`}
                />
              </div>

              <section className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm sm:p-4'>
                <div className='mb-4'>
                  <h2 className='text-lg font-semibold text-nova-foreground'>
                    Cumulative revenue (all time)
                  </h2>
                  <p className='mt-1 text-sm text-nova-muted-foreground'>
                    Running total of RevenueCat revenue (gross, minus refunds) from first recorded
                    revenue through today. Bucket size adapts to history length (daily, weekly, or
                    monthly).
                  </p>
                </div>
                <CumulativeRevenueChart
                  periods={data.periods}
                  currency={currency}
                  resolution={data.resolution}
                />
                <p className='mt-2 text-[11px] text-nova-muted-foreground'>
                  Hover a point for cumulative and period revenue.
                </p>
              </section>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/all-time')({
  component: AdminAllTimePage,
});
